/**
 * Lockout (UAT UI-017). PURE.
 *
 * A tracker or longitudinal study leaves out anyone who completed a previous
 * wave, by default: the surveys named under Lockout, or failing that the past
 * projects the check is calibrated with. The person can narrow it to a window
 * (people who completed in the last N days), widen it to every survey for this
 * client in a window, or turn it off. Any other study type has no lockout
 * unless one is asked for. The result says how many people it removed and how
 * that was counted (counts/lockout.ts).
 */
import { z } from 'zod';

import { isWaveStudy } from './waves';

export const LOCKOUT_MODES = ['previous_waves', 'client', 'off'] as const;
export type LockoutMode = (typeof LOCKOUT_MODES)[number];

/** The request schema's limit on surveys named in one place. */
export const MAX_LOCKOUT_SURVEYS = 50;

export const LockoutSchema = z
    .object({
        mode: z.enum(LOCKOUT_MODES),
        /** The previous waves, by survey number. */
        surveyIds: z.array(z.number().int().min(1)).max(MAX_LOCKOUT_SURVEYS).optional(),
        /** Only people who completed within this many days. Required for a client-wide lockout. */
        lastDays: z.number().int().min(1).max(730).optional(),
    })
    .refine((l) => l.mode !== 'client' || l.lastDays != null, {
        message: 'Say how far back the client lockout reaches.',
    });
export type Lockout = z.infer<typeof LockoutSchema>;

/** What a check's lockout leaves out, after defaults; null when nothing is left out. */
export interface EffectiveLockout {
    mode: 'previous_waves' | 'client';
    surveyIds: number[];
    lastDays: number | null;
    /** The client a client-wide lockout reaches, as the check names it. */
    client: string | null;
    /** Set by default for a wave study, not chosen. */
    byDefault: boolean;
    /** The waves came from the past projects the check is calibrated with. */
    fromCalibration: boolean;
}

interface LockoutInput {
    studyType: string;
    client?: string;
    lockout?: Lockout;
    calibration?: { projects: { projectId: string }[] };
}

const surveyNumbers = (r: LockoutInput) =>
    (r.calibration?.projects ?? [])
        .map((p) => Number(p.projectId))
        .filter((n) => Number.isInteger(n) && n > 0);

export function effectiveLockout(r: LockoutInput): EffectiveLockout | null {
    const l = r.lockout;
    if (l?.mode === 'off') return null;
    if (!l && !isWaveStudy(r.studyType)) return null;
    const named = [...new Set(l?.surveyIds ?? [])];
    const calibrated = named.length ? [] : surveyNumbers(r);
    return {
        mode: l?.mode ?? 'previous_waves',
        surveyIds: named.length ? named : calibrated,
        lastDays: l?.lastDays ?? null,
        client: l?.mode === 'client' ? (r.client?.trim() ?? null) : null,
        byDefault: !l,
        fromCalibration: !named.length && calibrated.length > 0,
    };
}

/** Why a lockout cannot be counted as asked, or null. */
export function lockoutIssue(r: LockoutInput): string | null {
    const e = effectiveLockout(r);
    if (e?.mode === 'client' && !e.client)
        return 'A client-wide lockout needs the client: name it at the top of the form.';
    return null;
}

const n = (x: number) => x.toLocaleString('en-US');
const days = (x: number) => `${n(x)} ${x === 1 ? 'day' : 'days'}`;

/** What the lockout leaves out, in a phrase: "people who completed surveys 2001 or 2002 in the last 180 days". */
export function lockoutWhat(e: EffectiveLockout): string {
    const within = e.lastDays ? ` in the last ${days(e.lastDays)}` : '';
    const waves = e.surveyIds.length
        ? `${e.surveyIds.length === 1 ? 'survey' : 'surveys'} ${e.surveyIds.join(', ')}`
        : null;
    if (e.mode === 'client')
        return `people who completed any ${e.client} survey${waves ? ` or ${waves}` : ''}${within}`;
    return `people who completed ${waves ?? 'a previous wave'}${within}`;
}

/** How the removed people were counted, by source (counts/lockout.ts). */
export type LockoutMethod = 'exact' | 'estimated' | 'not_counted';

export interface LockoutRead {
    /** People the audience lost to the lockout. Null when it could not be counted. */
    removed: number | null;
    /** People the lockout names, before they are matched to the audience. */
    named: number;
    method: LockoutMethod;
    /** For an estimate: the share of the visit data's visitors to these stores who are named. */
    share?: number;
}

/** The sentence the result shows. */
export function lockoutSentence(e: EffectiveLockout, read: LockoutRead): string {
    const lead = e.byDefault ? 'Lockout (on by default for a wave study)' : 'Lockout';
    if (!e.surveyIds.length && e.mode === 'previous_waves')
        return `${lead}: no previous waves are named, so nobody was left out. Name them under Lockout, or calibrate with past projects.`;
    const of = `the ${n(read.named)} ${lockoutWhat(e)}`;
    const source = e.fromCalibration
        ? ' The waves are the past projects this check is calibrated with.'
        : '';
    if (read.method === 'not_counted' || read.removed == null)
        return `${lead}: this data source cannot leave out ${of}, so the count may still include some of them.${source}`;
    if (read.method === 'estimated')
        return `${lead} left out about ${n(read.removed)} of ${of}. Estimated: ${Math.round((read.share ?? 0) * 1000) / 10}% of these stores' visitors in the visit data are among them.${source}`;
    return `${lead} left out ${n(read.removed)} of ${of}.${source}`;
}

/**
 * The count with `removed` people taken out, each login-recency bucket and
 * venue type reduced in proportion, so the funnel reads the same mix.
 */
export function withoutPeople<
    C extends {
        total: number;
        byRecency: { bucket: string; count: number }[];
        byVenueType?: { venueType: string; count: number }[];
    },
>(count: C, removed: number): C {
    const cut = Math.max(0, Math.min(count.total, Math.round(removed)));
    if (!cut || !count.total) return count;
    const keep = (count.total - cut) / count.total;
    const scale = <T extends { count: number }>(xs: T[]): T[] => {
        const target = Math.round(xs.reduce((n, x) => n + x.count, 0) * keep);
        const out = xs.map((x) => ({ ...x, count: Math.floor(x.count * keep) }));
        // Whole people: the rounding remainder goes to the largest buckets.
        let left = target - out.reduce((n, x) => n + x.count, 0);
        for (const i of out.map((_, i) => i).sort((a, b) => xs[b].count - xs[a].count)) {
            if (left <= 0) break;
            out[i].count++;
            left--;
        }
        return out;
    };
    return {
        ...count,
        total: count.total - cut,
        byRecency: scale(count.byRecency),
        ...(count.byVenueType ? { byVenueType: scale(count.byVenueType) } : {}),
    };
}
